import apiClient from './api';
import { track } from './analytics';

function getDownloadFileName(contentDisposition: string | undefined, fallbackTitle: string | undefined, contentType: string | undefined): string {
  const utf8Match = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
  const asciiMatch = contentDisposition?.match(/filename="?([^";]+)"?/i);
  const headerFileName = utf8Match?.[1] ?? asciiMatch?.[1];

  if (headerFileName) {
    return decodeURIComponent(headerFileName);
  }

  const safeTitle = (fallbackTitle ?? 'certificate')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

  const extension = contentType === 'application/pdf'
    ? 'pdf'
    : contentType === 'image/png'
      ? 'png'
      : contentType === 'image/jpeg'
        ? 'jpg'
        : 'bin';

  return `${safeTitle || 'certificate'}.${extension}`;
}

export interface UserComplianceSettings {
  complianceStateCode?: string;
  complianceStateName?: string;
  periodStart?: string;
  periodEnd?: string;
  isCustomPeriod: boolean;
  reportingPeriodType?: string;
  generalRequirementHours?: number;
}

export interface UpdateComplianceSettingsRequest {
  complianceStateCode?: string | null;
  customPeriodStart?: string | null;
  customPeriodEnd?: string | null;
}

export interface ReportingPeriod {
  stateCode?: string;
  stateName?: string;
  /** 'CalendarBased' | 'BirthdateBased' | 'LicenseDateBased' */
  reportingPeriodType: string;
  periodStart?: string;
  periodEnd?: string;
  generalRequirementHours?: number;
}

export interface CreditRecord {
  id: string;
  creditGroupId?: string;
  registrationId?: string;
  userId: string;
  courseId: number;
  courseTitle?: string;
  creditsEarned: number;
  dateEarned: string;
  category?: string;
  educationCategoryId?: number;
  courseType?: number; // 0 = OnDemand, 1 = Webinar
  certificateUrl?: string;
  source?: 'Platform' | 'External';
  /** Origin system for imported external credits ('picpa'); undefined for platform and manual credits. Imported credits are read-only. */
  externalSource?: string;
  /** Set when a spreadsheet upload created this manual credit (still fully editable). */
  importBatchId?: string;
  importedAt?: string;
  /** True when someone other than the owner (a firm admin) uploaded it. */
  importedByFirm?: boolean;
  providerName?: string;
  nasbaSponsorId?: string;
  deliveryMethod?: string;
  notes?: string;
  thirdPartyCreditId?: string;
}

/**
 * One external credit import source (PICPA/Aptify today, CPAx later) as it applies to the
 * current user — drives the one-time import prompt on CPE history and the Profile toggle.
 */
export interface CreditImportStatus {
  /** Source key, e.g. 'picpa'; also the value in CreditRecord.externalSource. */
  source: string;
  /** Human name for copy, e.g. 'PICPA'. */
  displayName: string;
  /** False until the source's API (or stub) is configured. The API only lists available sources. */
  available: boolean;
  autoSyncEnabled: boolean;
  lastSyncAt?: string | null;
  /** When the user answered the one-time import prompt (yes or no); null = not yet prompted. */
  promptedAt?: string | null;
  /**
   * Activities waiting to be imported. Only computed while the user hasn't been prompted;
   * null when not computed or the source was unreachable (the prompt then waits for a later visit).
   */
  pendingCreditCount?: number | null;
  /** Email the source's records are matched on. */
  email: string;
}

export interface CreditImportPreferences {
  autoSyncEnabled?: boolean;
  /** True once the user answered the one-time prompt ("No"; a "Yes" import stamps it itself). One-way. */
  markPrompted?: boolean;
}

export interface CreditImportResult {
  source: string;
  fetched: number;
  created: number;
  updated: number;
  unchanged: number;
  skippedExcluded: number;
  skippedInvalid: number;
  syncedAt: string;
}

export interface ThirdPartyCreditFormData {
  activityTitle: string;
  providerName?: string;
  nasbaSponsorId?: string;
  deliveryMethod?: string;
  categoryCredits: ThirdPartyCategoryCreditInput[];
  dateEarned: string; // YYYY-MM-DD
  notes?: string;
}

export interface ThirdPartyCategoryCreditInput {
  id?: string;
  educationCategoryId: number;
  creditHours: number;
}

export interface ThirdPartyCreditGroup {
  creditGroupId: string;
  userId: string;
  activityTitle: string;
  providerName?: string;
  nasbaSponsorId?: string;
  deliveryMethod?: string;
  dateEarned: string;
  notes?: string;
  createdAt: string;
  hasCertificate: boolean;
  totalCreditHours: number;
  credits: Array<{
    id: string;
    creditGroupId: string;
    userId: string;
    activityTitle: string;
    providerName?: string;
    nasbaSponsorId?: string;
    deliveryMethod?: string;
    creditHours: number;
    educationCategoryId?: number;
    educationCategory?: string;
    dateEarned: string;
    notes?: string;
    createdAt: string;
  }>;
}

export interface EducationCategory {
  id: number;
  name: string;
}

class CpeHistoryService {
  /**
   * Get the current user's CPE compliance settings (state + effective reporting period).
   */
  async getComplianceSettings(): Promise<UserComplianceSettings> {
    const response = await apiClient.get<UserComplianceSettings>('/api/cpe-history/compliance-settings');
    return response.data;
  }

  /**
   * Update the current user's CPE compliance state and/or custom reporting period.
   * Pass null values to clear overrides and revert to defaults.
   */
  async updateComplianceSettings(req: UpdateComplianceSettingsRequest): Promise<UserComplianceSettings> {
    const response = await apiClient.put<UserComplianceSettings>('/api/cpe-history/compliance-settings', req);
    return response.data;
  }

  /**
   * Get the CPE reporting period for the current user based on their state's requirements.
   * Returns null if the state has no calendar-based period configured.
   */
  async getReportingPeriod(): Promise<ReportingPeriod | null> {
    try {
      const response = await apiClient.get<ReportingPeriod>('/api/cpe-history/reporting-period');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch reporting period:', error);
      return null;
    }
  }

  /**
   * Get all credit records for the current authenticated user.
   * Returns both platform and third-party credits merged by date.
   */
  async getUserCredits(_userId?: string, _tenantId?: string): Promise<CreditRecord[]> {
    try {
      const response = await apiClient.get<{
        id: string;
        creditGroupId?: string;
        registrationId?: string;
        userId: string;
        courseId: number;
        courseTitle?: string;
        creditHours: number;
        awardedAt: string;
        educationCategoryId?: number;
        educationCategory?: string;
        notes?: string;
        courseType?: number;
        certificateUrl?: string;
        source?: string;
        externalSource?: string;
        importBatchId?: string;
        importedAt?: string;
        importedByFirm?: boolean;
        providerName?: string;
        nasbaSponsorId?: string;
        deliveryMethod?: string;
        thirdPartyCreditId?: string;
      }[]>('/api/cpe-history/credits');
      return response.data.map((c) => ({
        id: c.id,
        creditGroupId: c.creditGroupId,
        registrationId: c.registrationId,
        userId: c.userId,
        courseId: c.courseId,
        courseTitle: c.courseTitle,
        creditsEarned: c.creditHours,
        dateEarned: c.awardedAt,
        category: c.educationCategory,
        educationCategoryId: c.educationCategoryId,
        courseType: c.courseType,
        certificateUrl: c.certificateUrl,
        source: (c.source as 'Platform' | 'External') ?? 'Platform',
        externalSource: c.externalSource ?? undefined,
        importBatchId: c.importBatchId ?? undefined,
        importedAt: c.importedAt ?? undefined,
        importedByFirm: c.importedByFirm ?? undefined,
        providerName: c.providerName,
        nasbaSponsorId: c.nasbaSponsorId,
        deliveryMethod: c.deliveryMethod,
        notes: c.notes,
        thirdPartyCreditId: c.thirdPartyCreditId,
      }));
    } catch (error) {
      console.error('Failed to fetch user credits:', error);
      throw error;
    }
  }

  async getEducationCategories(): Promise<EducationCategory[]> {
    const response = await apiClient.get<EducationCategory[]>('/api/cpe-history/education-categories');
    return response.data;
  }

  /** Every available external credit import source with the current user's opt-in / prompt / last-import state. */
  async getCreditImports(): Promise<CreditImportStatus[]> {
    const response = await apiClient.get<CreditImportStatus[]>('/api/cpe-history/imports');
    return response.data;
  }

  /**
   * Import the current user's credits from one source now. Only ever called on the user's
   * explicit action. Add/update only; credits the user deleted earlier are never re-imported.
   */
  async runCreditImport(source: string): Promise<CreditImportResult> {
    const response = await apiClient.post<CreditImportResult>(`/api/cpe-history/imports/${encodeURIComponent(source)}/sync`);
    track('external_credits_imported', { source, created: response.data.created, updated: response.data.updated });
    return response.data;
  }

  /** Nightly opt-in and/or "prompt answered" for one source. Returns the updated status. */
  async updateCreditImportPreferences(source: string, preferences: CreditImportPreferences): Promise<CreditImportStatus> {
    const response = await apiClient.put<CreditImportStatus>(
      `/api/cpe-history/imports/${encodeURIComponent(source)}/preferences`,
      preferences,
    );
    return response.data;
  }

  async createThirdPartyCredit(data: ThirdPartyCreditFormData, certificate?: File): Promise<void> {
    const formData = new FormData();
    formData.append('activityTitle', data.activityTitle);
    if (data.providerName) formData.append('providerName', data.providerName);
    if (data.nasbaSponsorId) formData.append('nasbaSponsorId', data.nasbaSponsorId);
    if (data.deliveryMethod) formData.append('deliveryMethod', data.deliveryMethod);
    data.categoryCredits.forEach((entry, index) => {
      if (entry.id) formData.append(`categoryCredits[${index}].id`, entry.id);
      formData.append(`categoryCredits[${index}].educationCategoryId`, entry.educationCategoryId.toString());
      formData.append(`categoryCredits[${index}].creditHours`, entry.creditHours.toString());
    });
    formData.append('dateEarned', data.dateEarned);
    if (data.notes) formData.append('notes', data.notes);
    if (certificate) formData.append('certificate', certificate);

    await apiClient.post('/api/third-party-credits', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async updateThirdPartyCredit(
    creditGroupId: string,
    data: ThirdPartyCreditFormData,
    certificate?: File,
    removeCertificate?: boolean,
  ): Promise<void> {
    const formData = new FormData();
    formData.append('activityTitle', data.activityTitle);
    if (data.providerName) formData.append('providerName', data.providerName);
    if (data.nasbaSponsorId) formData.append('nasbaSponsorId', data.nasbaSponsorId);
    if (data.deliveryMethod) formData.append('deliveryMethod', data.deliveryMethod);
    data.categoryCredits.forEach((entry, index) => {
      if (entry.id) formData.append(`categoryCredits[${index}].id`, entry.id);
      formData.append(`categoryCredits[${index}].educationCategoryId`, entry.educationCategoryId.toString());
      formData.append(`categoryCredits[${index}].creditHours`, entry.creditHours.toString());
    });
    formData.append('dateEarned', data.dateEarned);
    if (data.notes) formData.append('notes', data.notes);
    if (removeCertificate) formData.append('removeCertificate', 'true');
    if (certificate) formData.append('certificate', certificate);

    await apiClient.put(`/api/third-party-credits/${creditGroupId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async deleteThirdPartyCredit(id: string): Promise<void> {
    await apiClient.delete(`/api/third-party-credits/${id}`);
  }

  async deleteThirdPartyCreditGroup(id: string): Promise<void> {
    await apiClient.delete(`/api/third-party-credits/${id}/group`);
  }

  async getThirdPartyCreditGroup(thirdPartyCreditId: string): Promise<ThirdPartyCreditGroup> {
    const response = await apiClient.get<ThirdPartyCreditGroup>(`/api/third-party-credits/${thirdPartyCreditId}/group`);
    return response.data;
  }

  async downloadCertificate(record: Pick<CreditRecord, 'certificateUrl' | 'source' | 'courseTitle' | 'courseType' | 'thirdPartyCreditId'>): Promise<void> {
    if (!record.certificateUrl) {
      throw new Error('Certificate is not available');
    }

    track('certificate_downloaded', {});

    // For External records, always use the backend proxy to avoid calling blob storage directly
    // (direct blob calls would add Authorization headers, triggering CORS preflight failures)
    if (record.source === 'External') {
      const proxyUrl = record.thirdPartyCreditId
        ? `/api/cpe-history/external-credits/${record.thirdPartyCreditId}/certificate`
        : record.certificateUrl;

      if (!proxyUrl.startsWith('/api/')) {
        // Last resort: certificateUrl is an absolute URL with no thirdPartyCreditId — cannot proxy safely
        throw new Error('Certificate is not available');
      }

      const response = await apiClient.get<Blob>(proxyUrl, { responseType: 'blob' });
      const contentTypeHeader = typeof response.headers['content-type'] === 'string'
        ? response.headers['content-type']
        : undefined;
      const contentDispositionHeader = typeof response.headers['content-disposition'] === 'string'
        ? response.headers['content-disposition']
        : undefined;
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: contentTypeHeader || 'application/octet-stream' });
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = getDownloadFileName(contentDispositionHeader, record.courseTitle, contentTypeHeader);
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      return;
    }

    // Platform records: use the certificateUrl as-is (already a relative /api/ path), or open externally
    const isApiPath = record.certificateUrl.startsWith('/api/');
    if (!isApiPath) {
      window.open(record.certificateUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const response = await apiClient.get<Blob>(record.certificateUrl, { responseType: 'blob' });
    const contentTypeHeader = typeof response.headers['content-type'] === 'string'
      ? response.headers['content-type']
      : undefined;
    const contentDispositionHeader = typeof response.headers['content-disposition'] === 'string'
      ? response.headers['content-disposition']
      : undefined;
    const blob = response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: contentTypeHeader || 'application/octet-stream' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = objectUrl;
    anchor.download = getDownloadFileName(
      contentDispositionHeader,
      record.courseTitle,
      contentTypeHeader,
    );

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
}

export default new CpeHistoryService();
